import React, { useState, useEffect, useMemo } from "react";
import {
  getTasks,
  getTasksByProjectId,
  updateTaskStatus,
} from "../../services/taskService";
import { Link, useParams } from "react-router-dom";
import { priorityColor } from "../../utils/priorityColor";

import {
  PencilSquareIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const TaskList = () => {
  const { id: projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("dueDate");

  /* ================== ADDED FILTER STATE ================== */
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  /* ======================================================== */

  useEffect(() => {
    async function loadTasks() {
      try {
        const data = projectId
          ? await getTasksByProjectId(projectId)
          : await getTasks();
        setTasks(data);
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
      } finally {
        setLoading(false);
      }
    }
    loadTasks();
  }, [projectId]);

  const handleUpdate = async (id, field, value) => {
    try {
      await updateTaskStatus(id, { [field]: value });
      setTasks((prev) =>
        prev.map((task) =>
          task.id === id ? { ...task, [field]: value } : task
        )
      );
    } catch (err) {
      console.error("Failed to update task", err);
    }
  };

  /* ================== FILTER HELPERS ================== */
  const projectOptions = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => map.set(t.project.name, t.project.name));
    return Array.from(map.values());
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const projectMatch =
        projectFilter === "all" ||
        task.project.name === projectFilter;

      const statusMatch =
        statusFilter === "all" ||
        task.status === statusFilter;

      return projectMatch && statusMatch;
    });
  }, [tasks, projectFilter, statusFilter]);
  /* ===================================================== */

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (sortKey === "dueDate")
      return new Date(a.deadline) - new Date(b.deadline);
    if (sortKey === "status") return a.status.localeCompare(b.status);
    if (sortKey === "project")
      return a.project.name.localeCompare(b.project.name);
    return 0;
  });

  if (loading)
    return <div className="text-center text-gray-600">Loading tasks...</div>;

  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Active Tasks</h1>
        <Link
          to="/tasks/add"
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          + New Task
        </Link>
      </div>

      {/* ================= FILTER BAR ================= */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-wrap gap-4 items-end">
        {/* Project Filter */}
        <div>
          <label className="block text-sm font-medium mb-1">Project</label>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="border px-3 py-2 rounded w-48"
          >
            <option value="all">All Projects</option>
            {projectOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border px-3 py-2 rounded w-40"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On Hold</option>
          </select>
        </div>

        {/* SORT */}
        <div>
          <label className="block text-sm font-medium mb-1">Sort By</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="border px-3 py-2 rounded"
          >
            <option value="dueDate">Due Date</option>
            <option value="status">Status</option>
            <option value="project">Project</option>
          </select>
        </div>

        {/* COUNT */}
        <div className="text-sm text-gray-600 ml-auto">
          Showing <strong>{sortedTasks.length}</strong> of{" "}
          <strong>{tasks.length}</strong>
        </div>
      </div>
      {/* ================================================= */}

      {/* ================= MOBILE VIEW ================= */}
      <div className="md:hidden space-y-4">
        {sortedTasks.length > 0 ? (
          sortedTasks.map((task) => (
            <div
              key={task.id}
              className="bg-white rounded-xl shadow-sm p-4 border"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold">{task.name}</h3>
                <span className="text-xs text-gray-500">
                  {task.deadline}
                </span>
              </div>

              <p className="text-sm text-gray-600 mt-1">
                Project: {task.project.name}
              </p>
              <p className="text-sm text-gray-600">
                Assigned: {task.assignTo.name}
              </p>

              <div className="flex gap-2 mt-3 flex-wrap">
                <select
                  value={task.priority}
                  onChange={(e) =>
                    handleUpdate(task.id, "priority", e.target.value)
                  }
                  className={`text-white text-xs px-2 py-1 rounded-full ${priorityColor(
                    task.priority
                  )}`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>

                <select
                  value={task.status}
                  onChange={(e) =>
                    handleUpdate(task.id, "status", e.target.value)
                  }
                  className="bg-gray-200 text-xs px-2 py-1 rounded-full"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-3">
                <Link to={`/tasks/edit/${task.id}`}>
                  <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                </Link>
                <Link to={`/tasks/${task.id}`}>
                  <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                </Link>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500">No tasks found.</p>
        )}
      </div>

      {/* ================= DESKTOP VIEW ================= */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full bg-white rounded-xl shadow-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="p-2 text-left">#</th>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Project</th>
              <th className="p-2 text-left">Assigned To</th>
              <th className="p-2 text-left">Priority</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Due Date</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task, index) => (
              <tr
                key={task.id}
                className="hover:bg-gray-50 border-t"
              >
                <td className="p-2">{index + 1}</td>
                <td className="p-2">{task.name}</td>
                <td className="p-2">{task.project.name}</td>
                <td className="p-2">{task.assignTo.name}</td>
                <td className="p-2">
                  <select
                    value={task.priority}
                    onChange={(e) =>
                      handleUpdate(task.id, "priority", e.target.value)
                    }
                    className={`text-white text-xs px-2 py-1 rounded-full ${priorityColor(
                      task.priority
                    )}`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </td>
                <td className="p-2">
                  <select
                    value={task.status}
                    onChange={(e) =>
                      handleUpdate(task.id, "status", e.target.value)
                    }
                    className="bg-gray-200 text-xs px-2 py-1 rounded-full"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                </td>
                <td className="p-2">{task.deadline}</td>
                <td className="p-2 flex gap-2">
                  <Link to={`/tasks/edit/${task.id}`}>
                    <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                  </Link>
                  <Link to={`/tasks/${task.id}`}>
                    <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TaskList;
